import { ThemeProvider } from "@emotion/react";
import {
  createTheme,
  CssBaseline,
  GlobalStyles,
  Snackbar,
  Stack,
  useMediaQuery,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";

import Header from "./Header";
import LoginPage from "./LoginPage";
import Main from "./Main";
import ProgressDialog from "./ProgressDialog";
import { TransferQueueProvider } from "./app/transferQueue";

const globalStyles = (
  <GlobalStyles styles={{ "html, body, #root": { height: "100%" } }} />
);

const lightTheme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#f38020" },
  },
});

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#f38020" },
    background: {
      default: "#121212",
      paper: "#1e1e1e",
    },
  },
});

export type ViewMode = "grid" | "list";
export type SortBy = "name" | "date" | "size";

function App() {
  const [search, setSearch] = useState("");
  const [showProgressDialog, setShowProgressDialog] = React.useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [sortBy, setSortBy] = useState<SortBy>("name");
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [colorMode, setColorMode] = useState<"light" | "dark">(() => {
    // Initialize from localStorage or default to system preference
    const saved = localStorage.getItem("colorMode") as "light" | "dark" | null;
    if (saved) return saved;
    // Default to light mode initially, will sync with system on mount
    return "light";
  });

  useEffect(() => {
    fetch("/api/session")
      .then((res) => res.json() as Promise<{ authenticated: boolean }>)
      .then((data) => setIsAuthenticated(data.authenticated))
      .catch(() => setIsAuthenticated(false));
  }, []);

  // Detect system dark mode preference
  const systemPrefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");

  useEffect(() => {
    // Only update if user hasn't set a manual preference
    if (!localStorage.getItem("colorMode")) {
      setColorMode(systemPrefersDarkMode ? "dark" : "light");
    }
  }, [systemPrefersDarkMode]);

  const handleColorModeToggle = () => {
    const newMode = colorMode === "light" ? "dark" : "light";
    setColorMode(newMode);
    localStorage.setItem("colorMode", newMode);
  };

  const theme = useMemo(
    () => (colorMode === "dark" ? darkTheme : lightTheme),
    [colorMode]
  );

  if (isAuthenticated === null) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {globalStyles}
      </ThemeProvider>
    );
  }

  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {globalStyles}
        <LoginPage onLoginSuccess={() => setIsAuthenticated(true)} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {globalStyles}
      <TransferQueueProvider>
        <Stack sx={{ height: "100%" }}>
          <Header
            search={search}
            onSearchChange={(newSearch: string) => setSearch(newSearch)}
            setShowProgressDialog={setShowProgressDialog}
            viewMode={viewMode}
            onLogout={() => {
              fetch("/api/logout").then(() => setIsAuthenticated(false));
            }}
            setViewMode={setViewMode}
            sortBy={sortBy}
            setSortBy={setSortBy}
            colorMode={colorMode}
            onColorModeToggle={handleColorModeToggle}
          />
          <Main search={search} onError={setError} viewMode={viewMode} sortBy={sortBy} />
        </Stack>
        <Snackbar
          autoHideDuration={5000}
          open={Boolean(error)}
          message={error?.message}
          onClose={() => setError(null)}
        />
        <ProgressDialog
          open={showProgressDialog}
          onClose={() => setShowProgressDialog(false)}
        />
      </TransferQueueProvider>
    </ThemeProvider>
  );
}

export default App;
